import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, Form, Row, Col, Button } from "react-bootstrap";
import Spinner from "../generics/Spinner";

const TASK_TYPES = [
  { value: "CLEANING", label: "Cleaning" },
  { value: "INSPECTION", label: "Inspection" },
  { value: "MAINTENANCE", label: "Maintenance" },
  { value: "SANITIZATION", label: "Sanitization" },
  { value: "SAFETY_CHECK", label: "Safety Check" },
  { value: "OTHER", label: "Other" },
];

const TARGET_TYPES = [
  { value: "MACHINE", label: "Machine" },
  { value: "FACILITY", label: "Facility" },
];

const FREQUENCIES = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "BIWEEKLY", label: "Bi-weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "BIANNUAL", label: "Bi-annual" },
  { value: "ANNUAL", label: "Annual" },
  { value: "CUSTOM", label: "Custom" },
];

const TaskTemplateForm = ({
  template,
  loading,
  error,
  isEdit,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    task_type: "CLEANING",
    target_type: "MACHINE",
    frequency: "WEEKLY",
    custom_frequency_days: "",
    estimated_duration_minutes: "",
    instructions: "",
    requires_photo: false,
    is_active: true,
  });
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (template && isEdit) {
      setFormData({
        name: template.name || "",
        description: template.description || "",
        task_type: template.task_type || "CLEANING",
        target_type: template.target_type || "MACHINE",
        frequency: template.frequency || "WEEKLY",
        custom_frequency_days: template.custom_frequency_days || "",
        estimated_duration_minutes: template.estimated_duration_minutes || "",
        instructions: template.instructions || "",
        requires_photo: template.requires_photo || false,
        is_active: template.is_active !== undefined ? template.is_active : true,
      });
    }
  }, [template, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    const submitData = {
      ...formData,
      estimated_duration_minutes: formData.estimated_duration_minutes
        ? parseInt(formData.estimated_duration_minutes, 10)
        : null,
      custom_frequency_days: formData.frequency === "CUSTOM" && formData.custom_frequency_days
        ? parseInt(formData.custom_frequency_days, 10)
        : null,
    };

    onSubmit(submitData);
  };

  if (loading && isEdit && !template) {
    return <Spinner />;
  }

  return (
    <div className="task-template-form">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <Link to="/maintenance/task-templates" className="text-decoration-none me-2">
            <i className="fa fa-arrow-left"></i>
          </Link>
          <i className="fa fa-clipboard me-2"></i>
          {isEdit ? "Edit Task Template" : "New Task Template"}
        </h4>
      </div>

      {error && (
        <div className="alert alert-danger mb-4">
          <i className="fa fa-exclamation-circle me-2"></i>
          {error.message || "Error saving task template"}
        </div>
      )}

      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Row>
          <Col md={8}>
            <Card className="mb-4">
              <Card.Header className="reports-filter">
                <i className="fa fa-info-circle me-2"></i>
                Basic Information
              </Card.Header>
              <Card.Body className="pt-4">
                <Form.Group className="mb-3">
                  <Form.Label>
                    Template Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Weekly Machine Inspection"
                  />
                  <Form.Control.Feedback type="invalid">
                    Name is required
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Brief description of this task template"
                  />
                </Form.Group>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Task Type
                      </Form.Label>
                      <Form.Select
                        name="task_type"
                        value={formData.task_type}
                        onChange={handleChange}
                      >
                        {TASK_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Target Type <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="target_type"
                        value={formData.target_type}
                        onChange={handleChange}
                        required
                      >
                        {TARGET_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Frequency
                      </Form.Label>
                      <Form.Select
                        name="frequency"
                        value={formData.frequency}
                        onChange={handleChange}
                      >
                        {FREQUENCIES.map((freq) => (
                          <option key={freq.value} value={freq.value}>
                            {freq.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {formData.frequency === "CUSTOM" && (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Custom Frequency (days) <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="custom_frequency_days"
                      value={formData.custom_frequency_days}
                      onChange={handleChange}
                      min="1"
                      required
                      placeholder="e.g., 10"
                    />
                    <Form.Control.Feedback type="invalid">
                      Custom frequency days is required when frequency is Custom
                    </Form.Control.Feedback>
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Estimated Duration (minutes)</Form.Label>
                  <Form.Control
                    type="number"
                    name="estimated_duration_minutes"
                    value={formData.estimated_duration_minutes}
                    onChange={handleChange}
                    min="1"
                    placeholder="e.g., 30"
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Header className="reports-filter">
                <i className="fa fa-list-ol me-2"></i>
                Instructions
              </Card.Header>
              <Card.Body className="pt-4">
                <Form.Group className="mb-3">
                  <Form.Label>Instructions</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="instructions"
                    value={formData.instructions}
                    onChange={handleChange}
                    placeholder="Detailed instructions for performing this task..."
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    name="requires_photo"
                    checked={formData.requires_photo}
                    onChange={handleChange}
                    label="Photo required for task completion"
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="mb-4">
              <Card.Header className="reports-filter">
                <i className="fa fa-toggle-on me-2"></i>
                Status
              </Card.Header>
              <Card.Body className="pt-4">
                <Form.Group>
                  <Form.Check
                    type="switch"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    label={formData.is_active ? "Active" : "Inactive"}
                  />
                  <Form.Text className="text-muted">
                    Inactive templates won't generate new tasks
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Header className="reports-filter">
                <i className="fa fa-info me-2"></i>
                Template Preview
              </Card.Header>
              <Card.Body className="pt-3">
                <table className="table table-sm mb-0">
                  <tbody>
                    <tr>
                      <th>Name</th>
                      <td>{formData.name || "-"}</td>
                    </tr>
                    <tr>
                      <th>Type</th>
                      <td>
                        {TASK_TYPES.find((t) => t.value === formData.task_type)?.label || "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Target</th>
                      <td>
                        {TARGET_TYPES.find((t) => t.value === formData.target_type)?.label || "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Frequency</th>
                      <td>
                        {FREQUENCIES.find((f) => f.value === formData.frequency)?.label || "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Duration</th>
                      <td>{formData.estimated_duration_minutes ? `${formData.estimated_duration_minutes} min` : "-"}</td>
                    </tr>
                    {formData.frequency === "CUSTOM" && (
                      <tr>
                        <th>Custom Days</th>
                        <td>{formData.custom_frequency_days || "-"}</td>
                      </tr>
                    )}
                    <tr>
                      <th>Photo</th>
                      <td>{formData.requires_photo ? "Required" : "Not required"}</td>
                    </tr>
                  </tbody>
                </table>
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-save me-2"></i>
                        {isEdit ? "Update Template" : "Create Template"}
                      </>
                    )}
                  </Button>
                  <Link
                    to="/maintenance/task-templates"
                    className="btn btn-outline-secondary"
                  >
                    <i className="fa fa-times me-2"></i>
                    Cancel
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default TaskTemplateForm;
